import React, { useState, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Home, ArrowLeft, Shield, Lock, AlertTriangle } from "react-feather";
import BaseButton from "../../BaseComponents/BaseButton";
import logoDark from "../../assets/images/logo/dark-based-logo-long.svg";
import logoLight from "../../assets/images/logo/white-based-logo-long.svg";
import "./forbidden.css";

// Generate particles outside component to avoid impure function calls during render
const generateParticles = () => {
  return Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    animationDelay: Math.random() * 5,
    size: Math.random() * 4 + 2,
  }));
};

const Forbidden = () => {
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains("dark"));

  // Generate particles once using useMemo with factory function
  const particles = useMemo(() => generateParticles(), []);

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const handleBack = () => navigate(-1);
  const handleHome = () => navigate("/");

  return (
    <div className="forbidden-container">
      {/* Animated background */}
      <div className="forbidden-bg">
        <div className="forbidden-gradient forbidden-gradient-1"></div>
        <div className="forbidden-gradient forbidden-gradient-2"></div>
        <div className="forbidden-gradient forbidden-gradient-3"></div>
      </div>

      {/* Floating particles */}
      <div className="forbidden-particles">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="forbidden-particle"
            style={{
              left: `${particle.left}%`,
              animationDelay: `${particle.animationDelay}s`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="forbidden-content">
        {/* Logo */}
        <img src={isDark ? logoDark : logoLight} alt="HR Fusions" className="forbidden-logo" />

        {/* Animated 403 with shield icon */}
        <div className="forbidden-error-section">
          <div className="forbidden-shield-container">
            <div className="forbidden-shield-glow"></div>
            <div className="forbidden-shield-icon">
              <Shield size={80} strokeWidth={1.5} />
              <Lock size={32} className="forbidden-lock-icon" />
            </div>
          </div>

          <div className="forbidden-code">
            <span className="forbidden-code-digit">4</span>
            <span className="forbidden-code-digit forbidden-code-zero">0</span>
            <span className="forbidden-code-digit">3</span>
          </div>
        </div>

        {/* Message */}
        <div className="forbidden-message">
          <h1 className="forbidden-title">Access Denied</h1>
          <p className="forbidden-subtitle">
            <AlertTriangle size={18} className="forbidden-alert-icon" />
            You don&apos;t have permission to access this page
          </p>
          <p className="forbidden-description">
            This area is restricted. If you believe this is an error, please contact your
            administrator.
          </p>
        </div>

        {/* Action buttons */}
        <div className="forbidden-actions">
          <BaseButton
            color="secondary"
            onClick={handleBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 24px",
              fontSize: "0.9375rem",
            }}
          >
            <ArrowLeft size={18} strokeWidth={2} />
            Go Back
          </BaseButton>
          <BaseButton
            color="primary"
            onClick={handleHome}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 24px",
              fontSize: "0.9375rem",
            }}
          >
            <Home size={18} strokeWidth={2} />
            Go Home
          </BaseButton>
        </div>

        {/* Decorative elements */}
        <div className="forbidden-decorations">
          <div className="forbidden-decoration forbidden-decoration-1"></div>
          <div className="forbidden-decoration forbidden-decoration-2"></div>
          <div className="forbidden-decoration forbidden-decoration-3"></div>
        </div>
      </div>
    </div>
  );
};

export default Forbidden;
